import { toast } from "vue3-toastify"

type AudioInstances = Record<string, HTMLAudioElement>
export interface VolumeOptions {
    volume?: number
    muted?: boolean
}
export interface AudioOptions extends VolumeOptions {
    loop?: boolean
    restart?: boolean
    currentTime?: number
    priority?: number
    onloadstart?: () => void
    onloadedmetadata?: (audioDuration: number) => void
    onplaying?: () => void
    ontimeupdate?: (audioCurrentTime: number) => void
    onpause?: () => void
    onended?: () => void
    onerror?: () => void
}

class AudioManager {
    private audios: AudioInstances = {}
    private readonly defaultOptions: Readonly<AudioOptions> = {
        volume: 1,
        muted: false,
        loop: false,
        restart: false,
        priority: 0
    }

    // Play audio and return the audio instance
    play(src: string, key: string = crypto.randomUUID(), _options?: AudioOptions): HTMLAudioElement {
        const options = {
            ...this.defaultOptions,
            ..._options
        }

        // Reuse or create new instance
        const existingAudio = this.audios[key]

        if (!existingAudio) {
            this.audios[key] = new Audio(src)
        }

        const audio = this.audios[key]

        if (decodeURI(audio.src) !== decodeURI(src)) {
            // trigger pause handler for previous component before attaching new one
            audio.onpause?.(new Event('pause'))
            audio.src = src
        }

        // handle options
        audio.loop = options.loop ? true : false
        audio.volume = options.volume || 1
        audio.muted = options.muted || false

        // store audio data for later use
        audio.dataset.originalVolume = String(options.volume || 1)
        audio.dataset.priority = String(options.priority || 0)

        const readyPlay = () => {
            if (typeof options.currentTime !== "undefined") {
                audio.currentTime = options.currentTime
            }

            if (options.restart && audio.currentTime || audio.currentTime >= audio.duration) {
                audio.currentTime = 0
            }

            audio.play().catch((err) => {
                toast.error(`${err.message}: ${src}`)
                audio.src = ''
            })
        }

        if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
            readyPlay()
        } else {
            audio.addEventListener('canplay', readyPlay, { once: true })
        }

        // set other audios volume based on priority
        if (options.priority) {
            const reduceVolumes = () => {
                Object.entries(this.audios).forEach(([otherKey, otherAudio]) => {
                    if (audio === otherAudio) return;

                    const otherPriority = Number(otherAudio.dataset.priority || 0)
                    if (otherPriority >= options.priority!) return;

                    // Reduce volume, but not to zero
                    this.setVolume(otherKey, Math.max(0.1, otherAudio.volume * 0.3))
                })
            }

            audio.removeEventListener('timeupdate', reduceVolumes)
            audio.addEventListener('timeupdate', reduceVolumes)

            // Restore volumes when playback pauses, ends, or fails
            const restoreVolumes = () => {
                Object.entries(this.audios).forEach(([otherKey, otherAudio]) => {
                    if (!otherAudio.dataset.originalVolume) return;
                    this.setVolume(otherKey, Number(otherAudio.dataset.originalVolume))
                })
            }

            // const stoppedEvents = ['ended', 'error', 'pause', 'abort', 'emptied', 'stalled', 'suspend']
            const stoppedEvents = ['pause', 'ended', 'error']

            const stopped = () => {
                restoreVolumes()
                // Remove event listeners after playback ends to avoid memory leaks
                stoppedEvents.forEach(event => audio.removeEventListener(event, stopped))
                audio.removeEventListener('timeupdate', reduceVolumes)
            }

            stoppedEvents.forEach(event => audio.addEventListener(event, stopped))
        }

        if (typeof options.onloadstart === "function") {
            audio.onloadstart = () => options.onloadstart?.()
        }
        if (typeof options.onloadedmetadata === "function") {
            audio.onloadedmetadata = () => options.onloadedmetadata?.(audio.duration)
        }
        if (typeof options.onplaying === "function") {
            audio.onplaying = () => options.onplaying?.()
        }
        if (typeof options.ontimeupdate === "function") {
            audio.ontimeupdate = () => options.ontimeupdate?.(audio.currentTime)
        }
        if (typeof options.onpause === "function") {
            audio.onpause = () => options.onpause?.()
        }
        if (typeof options.onended === "function") {
            audio.onended = () => options.onended?.()
        }
        if (typeof options.onerror === "function") {
            audio.onerror = () => options.onerror?.()
        }

        return audio
    }

    playShuffle(srcList: string[], key?: string, _options?: AudioOptions) {
        const playedIndexes = new Set<number>()

        const playNext = () => {
            if (playedIndexes.size >= srcList.length) {
                if (_options?.loop) {
                    playedIndexes.clear() // Reset if looping is enabled
                } else {
                    return // Stop if no loop
                }
            }

            // Get a random index that hasn't been played yet
            let index: number
            do {
                index = Math.floor(Math.random() * srcList.length)
            } while (playedIndexes.has(index))

            playedIndexes.add(index)

            // setting loop = false to trigger onended event for each playback to move on to the next audio
            const audio = this.play(srcList[index], key, { ..._options, loop: false })
            audio.onended = () => {
                index++
                playNext()
            }
        }

        playNext()
    }

    playIndex(srcList: string[], index: number, key?: string, _options?: AudioOptions) {
        this.play(srcList[index], key, _options)
    }

    setTime(key: string, time: number): HTMLAudioElement | undefined {
        const audio = this.audios[key]
        if (!audio) return;

        audio.currentTime = time
        return audio
    }

    pause(key: string): HTMLAudioElement | undefined {
        const audio = this.audios[key]
        if (!audio) return;

        audio.pause()
        return audio
    }

    stop(key: string): HTMLAudioElement | undefined {
        const audio = this.audios[key]
        if (!audio) return;

        audio.pause()
        audio.src = ''
        delete this.audios[key]
        return audio
    }

    stopAll() {
        Object.keys(this.audios).forEach((key) => this.stop(key))
    }

    getVolume(key: string): AudioOptions | undefined {
        const audio = this.audios[key]
        if (!audio) return;

        return {
            volume: audio?.volume,
            muted: audio?.muted
        }
    }

    setVolume(key: string, targetVolume: number, isOriginal: boolean = false, duration: number = 500): HTMLAudioElement | undefined {
        const audio = this.audios[key]
        if (!audio) return;

        if (audio.dataset.volumeInterval) {
            clearInterval(Number(audio.dataset.volumeInterval))
            delete audio.dataset.volumeInterval
        }

        const startVolume = audio.volume
        const volumeDifference = targetVolume - startVolume
        const stepTime = 50 // Interval in milliseconds
        const steps = duration / stepTime
        let currentStep = 0

        const volumeInterval = setInterval(() => {
            currentStep++
            const progress = currentStep / steps
            audio.volume = startVolume + volumeDifference * progress

            if (currentStep >= steps) {
                clearInterval(volumeInterval)
                audio.volume = targetVolume // Ensure the final volume is set
                if (isOriginal) {
                    audio.dataset.originalVolume = String(targetVolume)
                }
                delete audio.dataset.volumeInterval
            }
        }, stepTime)

        audio.dataset.volumeInterval = String(volumeInterval)

        return audio
    }

    setMuted(key: string, muted: boolean): HTMLAudioElement | undefined {
        const audio = this.audios[key]
        if (!audio) return;

        audio.muted = muted
        return audio
    }

    isPlaying(key: string): boolean {
        const audio = this.audios[key]
        return audio && !audio.paused
    }
}

export const audioManager = new AudioManager()